// PinnedSearch — search input that pins itself just above the
// iOS keyboard using the `.kb-sticky-input` opt-in class (the
// TikTok / iMessage composer pattern). Portal'd to document.body so
// the fixed positioning anchors to the viewport instead of being
// trapped by an ancestor with `backdrop-filter` or `transform`.
//
// History: this component previously maintained its own Capacitor +
// visualViewport listeners and tracked `kbHeight` in local state to
// position the portal-ed input. After the 2026-05-23 keyboard
// consolidation (commit 5d/5), the lift is owned by the global
// `.kb-sticky-input` class which reads `--kb-offset` (set by the
// single global useKeyboardOffset driver). No more per-component
// listeners.

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { COLOR } from "../../lib/tokens";

export function PinnedSearch({
  value,
  onChange,
  placeholder = "Search…",
}) {
  const [focused, setFocused] = useState(false);
  const portalInputRef = useRef(null);

  // When the portal mounts, focus its input so the keyboard stays
  // up and the user can keep typing.
  useEffect(() => {
    if (focused && portalInputRef.current) {
      try { portalInputRef.current.focus(); } catch {}
    }
  }, [focused]);

  const baseInputStyle = {
    width: "100%",
    padding: "12px 16px",
    fontFamily: "inherit",
    fontSize: 16,
    background: "#fff",
    border: `1px solid ${COLOR.border}`,
    borderRadius: 10,
    color: COLOR.ink,
    boxSizing: "border-box",
    outline: "none",
  };

  return (
    <>
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        onFocus={() => setFocused(true)}
        style={{
          ...baseInputStyle,
          visibility: focused ? "hidden" : "visible",
        }}
      />

      {focused && createPortal(
        <div
          className="kb-sticky-input"
          style={{
            // .kb-sticky-input handles `position: fixed; left: 0;
            // right: 0; bottom: calc(var(--kb-offset) + safe-area)`.
            // The 8px extra lift keeps the focused outline visible.
            paddingBottom: 8,
            paddingLeft: 12,
            paddingRight: 12,
            zIndex: 99999,
            pointerEvents: "auto",
          }}
        >
          <input
            ref={portalInputRef}
            type="search"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            onBlur={() => {
              // Tiny delay so a tap on a near button doesn't close
              // prematurely.
              setTimeout(() => setFocused(false), 50);
            }}
            style={{
              ...baseInputStyle,
              border: `2px solid ${COLOR.leaf}`,
              boxShadow: "0 -6px 24px rgba(0,0,0,0.25)",
            }}
          />
        </div>,
        document.body,
      )}
    </>
  );
}

export default PinnedSearch;
